import {
  HiAcademicCap,
  HiShoppingBag,
  HiShoppingCart,
  HiOfficeBuilding,
} from "react-icons/hi";
import {
  IoAirplane,
  IoMedkit,
  IoWater,
  IoRestaurant,
  IoCafe,
  IoCar,
} from "react-icons/io5";
import { GiPalmTree, GiHamburger, GiPineTree } from "react-icons/gi";
import { MdApartment } from "react-icons/md";

// Koordinat KinKen Kost Elite (Gg. Mawar No.8, Sidakarya)
export const MAIN_COORDINATES = {
  latitude: -8.700695839352965,
  longitude: 115.23376922591909,
  zoom: 15,
  pitch: 50,
  bearing: -10,
};

type Language = "id" | "en";

interface LocationData {
  id: number;
  name: { id: string; en: string };
  category: string;
  dist: string;
  desc: { id: string; en: string };
  coords: { lat: number; lng: number; zoom: number };
  icon: React.ComponentType<{ size?: number; className?: string }>;
  showOnHomepage: boolean;
  homepageOrder: number | null;
  travelMode: string;
  distanceKm: string;
}

// Daftar Semua Lokasi Strategis (Sidakarya Centric - Refined Coordinates)
const LOCATION_DATA_RAW: LocationData[] = [
  // === EDUCATION ===
  {
    id: 1,
    name: {
      id: "Universitas Pendidikan Nasional (UNDIKNAS)",
      en: "UNDIKNAS University",
    },
    category: "Education",
    dist: "3 Min",
    desc: {
      id: "Kampus favorit di Denpasar dengan ribuan mahasiswa. Jarak sangat dekat, ideal untuk mahasiswa.",
      en: "Popular university in Denpasar. Very close distance, ideal for students.",
    },
    coords: { lat: -8.69622, lng: 115.226383, zoom: 16 },
    icon: HiAcademicCap,
    showOnHomepage: true,
    homepageOrder: 1,
    travelMode: "car",
    distanceKm: "1.2 km",
  },

  // === SHOPPING & DAILY NEEDS ===
  {
    id: 2,
    name: {
      id: "Pasar Sidakarya (Pasar Sudha Merta)",
      en: "Sidakarya Traditional Market",
    },
    category: "Shopping",
    dist: "2 Min",
    desc: {
      id: "Pusat kuliner lokal murah meriah dan kebutuhan harian segar. Denyut nadi kehidupan lokal.",
      en: "Center for affordable local culinary and fresh daily necessities. Pulse of local life.",
    },
    coords: { lat: -8.7045, lng: 115.2352, zoom: 16 }, // Refined to likely market location on Jl. Sidakarya
    icon: HiShoppingCart,
    showOnHomepage: true,
    homepageOrder: 2,
    travelMode: "walk",
    distanceKm: "0.6 km",
  },
  {
    id: 3,
    name: { id: "Plaza Renon", en: "Plaza Renon" },
    category: "Shopping",
    dist: "8 Min",
    desc: {
      id: "Mall lifestyle chic dengan bioskop Cinemaxx, Starbucks, dan salon premium. Tempat hangout favorit.",
      en: "Chic lifestyle mall with Cinemaxx cinema, Starbucks, and premium salons. Favorite hangout spot.",
    },
    coords: { lat: -8.686111, lng: 115.240087, zoom: 15 },
    icon: HiShoppingBag,
    showOnHomepage: true,
    homepageOrder: 4,
    travelMode: "car",
    distanceKm: "2.8 km",
  },
  {
    id: 4,
    name: { id: "Level 21 Mall Denpasar", en: "Level 21 Mall Denpasar" },
    category: "Shopping",
    dist: "15 Min",
    desc: {
      id: "Mall terbesar & terhits di Denpasar. Pusat fashion H&M, Uniqlo, dan gym Celebrity Fitness.",
      en: "Largest & trendiest mall in Denpasar. Center for H&M, Uniqlo, and Celebrity Fitness gym.",
    },
    coords: { lat: -8.663889, lng: 115.228611, zoom: 14 },
    icon: HiOfficeBuilding,
    showOnHomepage: false,
    homepageOrder: null,
    travelMode: "car",
    distanceKm: "5.5 km",
  },

  // === BEACHES & RECREATION ===
  {
    id: 5,
    name: { id: "Pantai Mertasari (Sanur)", en: "Mertasari Beach (Sanur)" },
    category: "Recreation",
    dist: "10 Min",
    desc: {
      id: "Spot sunrise terbaik dan favorit lokal. Suasana tenang, cocok untuk jogging pagi dan sepedaan.",
      en: "Best sunrise spot and local favorite. Calm atmosphere, perfect for morning jogging and cycling.",
    },
    coords: { lat: -8.7107, lng: 115.2562, zoom: 15 }, // Entrance area of Mertasari Beach
    icon: IoWater,
    showOnHomepage: true,
    homepageOrder: 3,
    travelMode: "car",
    distanceKm: "4.5 km",
  },
  {
    id: 6,
    name: {
      id: "Pulau Serangan (Turtle Island)",
      en: "Serangan Island (Turtle Island)",
    },
    category: "Recreation",
    dist: "12 Min",
    desc: {
      id: "Konservasi penyu, water sport, dan wisata kuliner seafood di tepi pantai yang asri.",
      en: "Turtle conservation, water sports, and seafood culinary tourism on a beautiful beachfront.",
    },
    coords: { lat: -8.726, lng: 115.235, zoom: 13 },
    icon: GiPalmTree,
    showOnHomepage: false,
    homepageOrder: null,
    travelMode: "car",
    distanceKm: "5 km",
  },

  // === HEALTHCARE ===
  {
    id: 7,
    name: { id: "RSUD Bali Mandara", en: "Bali Mandara Hospital" },
    category: "Healthcare",
    dist: "10 Min",
    desc: {
      id: "Rumah sakit pemerintah bertaraf internasional. Fasilitas super lengkap dan modern.",
      en: "International standard public hospital. Super complete and modern facilities.",
    },
    coords: { lat: -8.7035617, lng: 115.2486742, zoom: 16 }, // Main Lobby area
    icon: IoMedkit,
    showOnHomepage: true,
    homepageOrder: 5,
    travelMode: "car",
    distanceKm: "4.8 km",
  },
  {
    id: 8,
    name: { id: "RSUP Sanglah", en: "Sanglah General Hospital" },
    category: "Healthcare",
    dist: "18 Min",
    desc: {
      id: "Rumah sakit rujukan utama terbesar di Bali.",
      en: "Bali's largest main referral hospital.",
    },
    coords: { lat: -8.6725, lng: 115.228611, zoom: 15 },
    icon: IoMedkit,
    showOnHomepage: false,
    homepageOrder: null,
    travelMode: "car",
    distanceKm: "6.5 km",
  },

  // === TRANSPORT ===
  {
    id: 9,
    name: {
      id: "Gerbang Tol Benoa (Pesanggaran)",
      en: "Benoa Toll Gate (Pesanggaran)",
    },
    category: "Transport",
    dist: "8 Min",
    desc: {
      id: "Akses cepat ke Bandara, Nusa Dua, dan Jimbaran via Tol Bali Mandara. Pintu masuk terdekat dari Sidakarya.",
      en: "Fast access to Airport, Nusa Dua, and Jimbaran via Bali Mandara Toll Road. Closest entry from Sidakarya.",
    },
    coords: { lat: -8.7205, lng: 115.2155, zoom: 14 }, // Refined to Pesanggaran Gate
    icon: IoCar,
    showOnHomepage: true,
    homepageOrder: 6,
    travelMode: "car",
    distanceKm: "3.5 km",
  },
  {
    id: 10,
    name: {
      id: "Bandara I Gusti Ngurah Rai",
      en: "I Gusti Ngurah Rai Airport",
    },
    category: "Transport",
    dist: "25 Min",
    desc: {
      id: "Gerbang utama Bali. Akses mudah via Tol Bali Mandara hindari macet.",
      en: "Bali's main gateway. Easy access via Bali Mandara Toll to avoid traffic.",
    },
    coords: { lat: -8.748168, lng: 115.167275, zoom: 12 },
    icon: IoAirplane,
    showOnHomepage: false,
    homepageOrder: null,
    travelMode: "car",
    distanceKm: "12 km",
  },

  // === BUSINESS & DINING ===
  {
    id: 11,
    name: { id: "Renon Civic Center", en: "Renon Civic Center" },
    category: "Business",
    dist: "10 Min",
    desc: {
      id: "Pusat pemerintahan dan perkantoran. Lapangan Puputan untuk jogging sore.",
      en: "Government and office center. Puputan Field for afternoon jogging.",
    },
    coords: { lat: -8.681667, lng: 115.2325, zoom: 15 },
    icon: MdApartment,
    showOnHomepage: false,
    homepageOrder: null,
    travelMode: "car",
    distanceKm: "3.5 km",
  },
  {
    id: 12,
    name: { id: "Kuliner Malam Bedugul", en: "Bedugul Night Culinary" },
    category: "Dining",
    dist: "3 Min",
    desc: {
      id: "Pusat jajanan mahasiswa Undiknas di Jl. Bedugul. Murah, enak, dan buka sampai malam.",
      en: "Undiknas student snack hub at Jl. Bedugul. Cheap, delicious, and open late.",
    },
    coords: { lat: -8.6975, lng: 115.2275, zoom: 16 }, // Approx Jl. Bedugul near Undiknas
    icon: GiHamburger,
    showOnHomepage: false,
    homepageOrder: null,
    travelMode: "walk",
    distanceKm: "1 km",
  },
  {
    id: 13,
    name: {
      id: "Area Coffee Shop Sidakarya",
      en: "Sidakarya Coffee Shop Area",
    },
    category: "Dining",
    dist: "5 Min",
    desc: {
      id: "Deretan coffee shop hits (Kopi Studio 24, dll) untuk nugas atau nongkrong.",
      en: "Row of trendy coffee shops (Kopi Studio 24, etc) for working or hanging out.",
    },
    coords: { lat: -8.7055, lng: 115.2305, zoom: 16 }, // Approx Jl. Sidakarya central
    icon: IoCafe,
    showOnHomepage: false,
    homepageOrder: null,
    travelMode: "car",
    distanceKm: "1.5 km",
  },
];

// Helper function untuk mendapatkan LOCATION_DATA dengan bahasa
export const getLocationData = (lang: Language) => {
  return LOCATION_DATA_RAW.map((location) => ({
    ...location,
    name: location.name[lang],
    desc: location.desc[lang],
  }));
};

// Helper function untuk mendapatkan lokasi homepage
export const getHomepageLocations = (lang: Language) => {
  return getLocationData(lang)
    .filter((location) => location.showOnHomepage === true)
    .sort((a, b) => (a.homepageOrder || 99) - (b.homepageOrder || 99));
};

// Helper function untuk mendapatkan lokasi by category
export const getLocationsByCategory = (category: string, lang: Language) => {
  return getLocationData(lang).filter(
    (location) => location.category === category,
  );
};

// Legacy exports for backward compatibility
export const LOCATION_DATA = LOCATION_DATA_RAW.map((location) => ({
  ...location,
  name: location.name.id,
  desc: location.desc.id,
}));

// Helper function untuk toggle lokasi di homepage
export const toggleHomepageLocation = (id: number, show: boolean) => {
  const index = LOCATION_DATA_RAW.findIndex((loc) => loc.id === id);
  if (index !== -1) {
    LOCATION_DATA_RAW[index].showOnHomepage = show;
  }
};

// Helper function untuk update homepage order
export const updateHomepageOrder = (id: number, newOrder: number) => {
  const index = LOCATION_DATA_RAW.findIndex((loc) => loc.id === id);
  if (index !== -1) {
    LOCATION_DATA_RAW[index].homepageOrder = newOrder;
  }
};
